import { useCallback, useEffect, useState } from "react";
import userApi, { DigiAuthResponse } from "../api/user";
import { DigiAuthUser } from "../types/user";
import { trackGTM, generateDataLayerEventObject, convertToDataLayerName } from "../utils/trackGTM";
import { trackGAEventforPHD, generateRegisterByIdEventObject } from "../utils/trackGAEventforPHD";
import { getLocalLoan } from "../utils/loan";

const checkInterval = 2000;

export type DigiAuthState =
  | { status: "idle" }
  | { status: "starting" }
  | { status: "running", controlCode: string, sessionId: string }
  | { status: "error", message: string }
  | { status: "success" };

type StartDigiAuth = (user: DigiAuthUser) => Promise<void | FormErrors>;

export type DigiAuthHookResult = [DigiAuthState, StartDigiAuth, VoidFunction];

export const useDigiAuth = (): DigiAuthHookResult => {
  const [authState, setAuthState] = useState<DigiAuthState>({ status: "idle" });
  const [timer, setTimer] = useState<null | number>(null);
  const localLoan = getLocalLoan();

  useEffect(() => () => { if (timer) clearTimeout(timer) }, [timer]);

  const onRefreshSuccess = useCallback((data: DigiAuthResponse) => {
    setAuthState((oldState: DigiAuthState) => {
      if (oldState.status !== "running") return oldState; // something happened during the request
      if (data.user) return { status: "success" }
      if (data.state === "running") setTimer(setTimeout(refreshMobileId, checkInterval, oldState.sessionId));
      return oldState;
    });
  }, [setAuthState, setTimer]);

  const onRefreshFailure = useCallback((e: { error?: unknown }) => {
    setAuthState((oldState: DigiAuthState) => {
      if (oldState.status !== "running") return oldState; // something happened during the request
      return { status: "error", message: String(e.error) };
    });
  }, [setAuthState]);

  const refreshMobileId = useCallback((sid: string): void => {
    userApi.getDigiAuthUser(sid).then(onRefreshSuccess).catch(onRefreshFailure);
  }, [onRefreshSuccess, onRefreshFailure]);

  const startAuth: StartDigiAuth = useCallback(async (user: DigiAuthUser): Promise<void | FormErrors> => {
    setAuthState({ status: "starting" });
    return userApi.startDigiAuth(user)
      .then(({ code, sessionId }) => {
        setAuthState((oldState: DigiAuthState) => {
          if (oldState.status !== "starting") return oldState; // something happened during the request
          setTimer(setTimeout(refreshMobileId, checkInterval, sessionId));
          return { status: "running", controlCode: code, sessionId };
        })
        if (user.role === "borrower" && !user.signIn) {
          trackGTM({
            ...generateDataLayerEventObject('create_account'),
            item_name: convertToDataLayerName(localLoan?.purpose ?? null),
          });
        }
        if (!user.signIn) {
          trackGAEventforPHD(generateRegisterByIdEventObject(user));
        }
      })
      .catch((errors: FormErrors) => {
        setAuthState({ status: "idle" });
        return errors;
      })
  }, [setAuthState, setTimer]);

  const stopAuth = (): void => {
    setAuthState({status: "idle"});
  }

  return [authState, startAuth, stopAuth];
}
